import { AfterContentInit, Component, ContentChildren, EventEmitter, Input, NgModule, Output, QueryList, TemplateRef, ViewChild, inject } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { PrimeTemplate } from 'primeng/api';
import { AppLayoutModule } from '../../modules/app-layout/app-layout.module';
import { SaraPlusFormModule } from '@sp-forms/saraplus-ngx-forms';
import { AppBaseModule } from '../../modules/app-base.module';
import { DataService } from '../../services/data.service';
import { EnvironmentService } from '../../services/environment.service';
import { take } from 'rxjs';
import { StateService } from '../../services/state.service';
import { NotificationService } from '../../services/notification.service';
import { OtpVerificationModalComponent, OtpVerificationModalModule } from '../otp-verification-modal/opt-verification-modal.component';
import { TranslationModule } from '../translation.module';

export enum EmailVerifyStatus {
  VerifiedByCustomer,
  Verified,
  NotVerified,
  Invalid,
  InProgress
}
@Component({
    selector: 'app-email-verification',
    templateUrl: './email-verification.component.html',
    styleUrl: './email-verification.component.scss',
    standalone: false
})
export class EmailVerificationComponent implements AfterContentInit {


  @Input() formGroupRef: FormGroup | undefined;
  @Input() controlName: string | undefined;
  @Input() control: AbstractControl | undefined;
  @Input() styleClass: string = '';
  @Input() iconStyleClass: string = '';
  @Input() showSearchIcon = true;
  @Input() showLabel = true;
  @Input() hideValidatePhone = true;
  @Input() isAlreadyVerifiedByCustomer = false;
  @Output() OnSearchClicked: EventEmitter<Event> = new EventEmitter();
  @Output() verificationStatus: EventEmitter<EmailVerifyStatus> = new EventEmitter();
  @Output() ChangeInputFocus: EventEmitter<unknown> = new EventEmitter();
  @Output() ChangeInputBlur: EventEmitter<void> = new EventEmitter();

  @ViewChild('optVerificationModal') optVerificationModal?: OtpVerificationModalComponent
  @ContentChildren(PrimeTemplate) templates: QueryList<PrimeTemplate> | undefined;

  #dataService = inject(DataService)
  #environmentService = inject(EnvironmentService)
  #stateService = inject(StateService)
  #notificationService = inject(NotificationService)

  isContentLoaded = false;
  isInProgress = false
  labelTemplate: TemplateRef<any> | undefined;
  controlTemplate: TemplateRef<any> | undefined;
  status: EmailVerifyStatus = EmailVerifyStatus.Invalid;
  lastVerifiedValue?: string;
  typingTimeout: any | undefined;
  showOtpDialog = false;

  ngAfterContentInit(): void {
    this.mapTemplates();
    this.registerInputChanges();
    if (this.isAlreadyVerifiedByCustomer) this.status = EmailVerifyStatus.VerifiedByCustomer;
    else this.status = EmailVerifyStatus.Verified;
    this.isContentLoaded = true;
  }

  mapTemplates() {
    if (this.templates) {
      (this.templates as QueryList<PrimeTemplate>).forEach(item => {
        switch (item.getType()) {
          case 'label':
            this.labelTemplate = item.template;
            break;
          case 'control':
            this.controlTemplate = item.template;
            break;
        }
      });
    }
  }

  registerInputChanges() {
    if (this.control) {
      this.control.valueChanges.subscribe({
        next: () => {
          if (!this.control!.valid) {
            this.status = EmailVerifyStatus.Invalid
          }
        }
      })
    }
  }

  onInputChange() {

  }

  onInputBlur() {
    this.updateStatus();
  }

  verifyEmail(email: string) {
    this.status = EmailVerifyStatus.InProgress;
    console.log(email);
    const emailVerifyUrl = this.#environmentService.getUrl('securedata/validateemail/');
    const endpoint = `${emailVerifyUrl}/${email}`;

    this.#dataService.get<number>(endpoint).pipe(take(1)).subscribe({
      next: result => {
        switch (result) {
          case 0: // Valid - Green
            this.status = EmailVerifyStatus.Verified;
            this.control?.setErrors(null);
            this.control?.updateValueAndValidity();
            break;
          case 1: // Risky - Yellow
            this.status = EmailVerifyStatus.NotVerified;
            this.control?.setErrors(null); // Don't set as invalid since it's risky but not completely invalid
            this.control?.updateValueAndValidity();
            break;
          case 2: // Invalid - Red
          default:
            this.status = EmailVerifyStatus.Invalid;
            this.control?.setErrors({ 'invalid': true });
            break;
        }
        this.verificationStatus.emit(this.status);
      },
      error: () => {
        this.status = EmailVerifyStatus.Invalid;
        this.verificationStatus.emit(this.status);
      }
    })
  }

  get shouldShowValidateButton(): boolean {
    return this.control?.valid &&
      this.status !== EmailVerifyStatus.VerifiedByCustomer &&
      this.showLabel;
  }

  get statusIcon() {
    if (!this.isContentLoaded) return undefined;
    let icon = 'pi-email-red-sara';
    switch (this.status) {
      case EmailVerifyStatus.Invalid:
        icon = 'pi-email-red-sara'
        break;
      case EmailVerifyStatus.VerifiedByCustomer:
        icon = 'pi-email-green-sara-verified'
        break;
      case EmailVerifyStatus.Verified:
        icon = 'pi-email-green-sara'
        break;
      case EmailVerifyStatus.NotVerified:
        icon = 'pi-email-yellow-sara'
        break;
      case EmailVerifyStatus.InProgress:
        icon = 'pi-spinner pi-spin'
        break;
    }
    return icon;
  }

  validate() {
    if (this.control?.valid) {
      const endpoint = this.#environmentService.getUrl('securedata/validatecontact');
      const body = {
        UniqueId: this.#stateService.ConnectionId,
        ContactEmail: this.control?.value
      }
      this.#dataService.post<unknown, void>(endpoint, body).pipe(take(1)).subscribe({
        next: () => {
          this.#notificationService.showInfo("OTP sent successfully.", "Information");
          this.showOtpDialog = true;
        }
      })
    }
  }

  onPinValidate(pin?: string) {
    if (pin) {
      const endpoint = this.#environmentService.getUrl('securedata/validatecontactpin');
      const body = {
        UniqueId: this.#stateService.ConnectionId,
        ContactEmail: this.control?.value,
        Pin: +pin
      }
      this.#dataService.post<unknown, void>(endpoint, body, undefined, true).pipe(take(1)).subscribe({
        next: () => {
          this.#notificationService.showInfo("OTP verified.", "Information");
          this.optVerificationModal?.onPinValidated(true);
          this.status = EmailVerifyStatus.VerifiedByCustomer;
          this.lastVerifiedValue = this.control?.value;
          this.verificationStatus.emit(this.status);
        },
        error: (err) => {
          if (err) {
            this.optVerificationModal?.onPinValidated(false, err.error.ErrorDescription);
          }
        }
      })
    }
  }

  updateStatus() {

    if (!this.control) return;
    const value = this.control.value as string;
    if (this.control.errors?.['email'] || this.control.errors?.['pattern'] || this.control.errors?.['required']) {
      this.status = EmailVerifyStatus.Invalid
    }
    else if (!this.isLastVerifiedValue) {
      this.verifyEmail(value);
    } else if (this.isLastVerifiedValue) {
      this.status = EmailVerifyStatus.VerifiedByCustomer;
    }
  }

  get isLastVerifiedValue() {
    return this.control?.value && this.lastVerifiedValue?.toLowerCase() == (this.control.value as string).toLowerCase();
  }

  onInputFocus() {
    this.ChangeInputFocus.emit('email');
  }
}

@NgModule({
  declarations: [
    EmailVerificationComponent
  ],
  imports: [
    AppBaseModule,
    SaraPlusFormModule,
    AppLayoutModule,
    OtpVerificationModalModule,
    TranslationModule
  ],
  exports: [
    EmailVerificationComponent
  ],
  providers: [
    EnvironmentService
  ]
})
export class EmailVerificationModule {

}
